import { useEffect, useRef, useState } from 'react';
import { shouldShowSignUp } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion, useInView, useAnimation } from 'framer-motion';
import {
  BarChart3,
  CheckCircle2,
  ChevronRight,
  Shield,
  Database,
  Upload,
  Brain,
  Book,
  UserCheck,
  AlertCircle,
  PieChart,
  Globe,
  FileCheck,
  Layers,
  Lock,
  Code,
  Network,
  LineChart,
  Building,
  CloudCog,
  Rocket,
} from 'lucide-react';

import { NavBar } from '@/components/navbar';
import { Footer } from '@/components/SiteFooter';
import FeatureSection from '@/components/features/FeatureSection';
import LanguageSection from '@/components/features/LanguageSection';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Helmet } from 'react-helmet-async';

const FeaturesPage = () => {
  const { t } = useTranslation('common');
  const showSignUp = shouldShowSignUp();

  // Animation controls for scroll animations
  const controls = useAnimation();
  const featuresRef = useRef(null);
  const featuresInView = useInView(featuresRef, { once: true });

  useEffect(() => {
    document.title = 'Features | Verdea Regulatory Compliance Platform';

    if (featuresInView) {
      controls.start('visible');
    }
  }, [controls, featuresInView]);

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
  };

  // Updated CSRD features
  const documentFeatures = {
    title: 'Transform Your Documents into Regulatory Compliance',
    description: 'Seamless Document Integration and AI-Powered Analysis',
    features: [
      {
        icon: Upload,
        title: 'Seamless Document Integration',
        description:
          'Upload Excel, Word, and PDF files directly into our platform. Our AI instantly analyzes and extracts relevant regulatory data, saving weeks of manual work.',
      },
      {
        icon: Brain,
        title: 'Custom RAG LLM for Precise Insights',
        description:
          'Our proprietary AI model, trained on EU directives and regulations, provides context-aware insights and recommendations tailored to your specific business context.',
      },
      {
        icon: Book,
        title: 'Comprehensive EU Directive Coverage',
        description:
          'From CSRD to NIS2 and AIFMD, our platform adapts to evolving EU regulations, future-proofing your compliance efforts across multiple directives.',
      },
    ],
  };

  // Updated security features
  const securityFeatures = {
    title: 'Enterprise-Grade Security and Privacy',
    description:
      'Protecting your sensitive regulatory data with state-of-the-art security measures',
    features: [
      {
        icon: Shield,
        title: 'End-to-End Encryption',
        description:
          'All data is encrypted using AES-256 in transit and at rest, ensuring confidentiality. Our protocols protect against unauthorized access and data breaches.',
      },
      {
        icon: Database,
        title: 'Data Sovereignty',
        description:
          'Our EU-based data centers ensure data never leaves European jurisdiction, complying with GDPR. This approach simplifies regulatory compliance for all your reporting needs.',
      },
      {
        icon: UserCheck,
        title: 'Rigorous Access Controls',
        description:
          'Implement role-based access controls and multi-factor authentication to safeguard sensitive data. Comprehensive audit logs offer transparency for monitoring.',
      },
    ],
  };

  // Updated CSRD compliance features
  const csrdFeatures = {
    title: 'Streamlined CSRD Compliance',
    description: 'Intuitive Tools for Comprehensive Sustainability Reporting',
    features: [
      {
        icon: BarChart3,
        title: 'CSRD Progress Dashboard',
        description:
          'Visualize your compliance journey with our intuitive progress tracker that highlights completed and pending requirements.',
      },
      {
        icon: AlertCircle,
        title: 'AI-Powered Double Materiality Assessment',
        description:
          'Generate ESRS impact statements with AI-calculated probabilities and evidence-based materiality analysis.',
      },
      {
        icon: PieChart,
        title: 'Impact Materiality Analysis',
        description:
          'Identify and categorize material impacts as per CSRD ESRS, enabling focused sustainability reporting.',
      },
    ],
  };

  // New NIS2 compliance features
  const nis2Features = {
    title: 'Comprehensive NIS2 Compliance',
    description: 'Advanced Cybersecurity Risk Management and Incident Reporting',
    features: [
      {
        icon: Network,
        title: 'Cybersecurity Risk Assessment',
        description:
          'Automatically identify and evaluate potential cybersecurity risks with our AI-powered risk assessment engine.',
      },
      {
        icon: CloudCog,
        title: 'Incident Response Management',
        description:
          'Streamline incident reporting with automated workflows that ensure timely notification to relevant authorities.',
      },
      {
        icon: Lock,
        title: 'Supply Chain Security Evaluation',
        description:
          'Assess and monitor third-party security risks with comprehensive supplier risk profiles and continuous monitoring.',
      },
    ],
  };

  // New AIFMD compliance features
  const aifmdFeatures = {
    title: 'AIFMD Compliance Made Simple',
    description: 'Comprehensive Alternative Investment Fund Reporting and Risk Management',
    features: [
      {
        icon: LineChart,
        title: 'Risk Management Reporting',
        description:
          'Automate your risk management reporting with AI-powered analysis of fund performance and market conditions.',
      },
      {
        icon: Building,
        title: 'Regulatory Update Implementation',
        description:
          'Stay compliant with evolving AIFMD requirements through automatic regulatory updates and implementation guidance.',
      },
      {
        icon: Globe,
        title: 'Cross-Border Compliance',
        description:
          'Navigate complex multi-jurisdictional requirements with country-specific reporting templates and validation.',
      },
    ],
  };

  // New custom workflow features
  const customWorkflowFeatures = {
    title: 'Custom AI Workflows',
    description: 'Tailor Compliance Processes to Your Unique Business Needs',
    features: [
      {
        icon: Code,
        title: 'Workflow Customization',
        description:
          "Design bespoke compliance workflows that align perfectly with your organization's structure and reporting needs.",
      },
      {
        icon: Layers,
        title: 'Multi-Framework Integration',
        description:
          'Seamlessly integrate multiple regulatory frameworks into a unified compliance program that eliminates redundant tasks.',
      },
      {
        icon: Rocket,
        title: 'Automated Reporting',
        description:
          'Generate custom reports automatically with our AI engine, formatted exactly to your specifications and stakeholder requirements.',
      },
    ],
  };

  // All features combined for the detailed section
  const allFeatures = [
    ...csrdFeatures.features,
    ...nis2Features.features,
    ...aifmdFeatures.features,
    ...customWorkflowFeatures.features,
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Features | Verdea Regulatory Compliance Platform</title>
        <meta
          name="description"
          content="Explore Verdea's powerful features for regulatory compliance - AI document processing, security controls, and intuitive reporting tools for CSRD, NIS2, AIFMD and more"
        />
      </Helmet>

      <NavBar />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-50 to-white pt-24 pb-16">
        <div className="absolute inset-0 z-0 opacity-40">
          <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <pattern id="grid" width="8" height="8" patternUnits="userSpaceOnUse">
                <path
                  d="M 8 0 L 0 0 0 8"
                  fill="none"
                  stroke="rgba(15, 61, 43, 0.05)"
                  strokeWidth="0.5"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-br from-[#1A1F2C] to-[#0F3D2B] bg-clip-text text-transparent">
              Transform Regulatory Requirements Into Strategic Advantages
            </h1>
            <p className="text-xl text-slate-700 mb-8 leading-relaxed max-w-3xl mx-auto">
              Discover how Verdea's AI-powered platform streamlines compliance across CSRD, NIS2,
              AIFMD and other regulatory frameworks.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book-demo">
                <Button size="lg" className="bg-emerald-700 hover:bg-emerald-800 text-white">
                  Book a Demo
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/learn-more">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-emerald-700 text-emerald-700 hover:bg-emerald-50"
                >
                  Learn More
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-xl"
          >
            <div className="aspect-w-16 aspect-h-9 relative">
              <video
                className="w-full h-full object-cover"
                autoPlay
                loop
                muted
                playsInline
                poster="/screens/dashboard_overview.png"
              >
                <source src="/videos/video-csrd-data.mp4" type="video/mp4" />
                {/* Only include MOV source for iOS/macOS devices */}
                {/iPhone|iPad|iPod|Macintosh/i.test(navigator.userAgent) && (
                  <source src="/videos/video-csrd-data.mov" type="video/quicktime" />
                )}
                Your browser does not support the video tag.
              </video>
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent flex items-end">
                <div className="p-6 text-white">
                  <h3 className="text-xl font-semibold mb-2">See Verdea in Action</h3>
                  <p className="text-sm opacity-90">
                    Transform complex compliance requirements into strategic insights
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Feature Tabs Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900">
              Comprehensive Regulatory Coverage
            </h2>
            <p className="text-lg text-slate-600">
              Explore our powerful tools that make regulatory compliance simple and efficient across
              multiple frameworks
            </p>
          </motion.div>

          <div className="relative max-w-4xl mx-auto">
            {/* Apple-style Tabs */}
            <Tabs defaultValue="csrd" className="w-full">
              {/* Apple-inspired Tab Navigation */}
              <div className="relative flex justify-center mb-16">
                <div className="absolute bottom-0 w-full border-b border-gray-200"></div>
                <TabsList className="relative bg-transparent border-0 flex space-x-4 p-0 z-10 overflow-x-auto max-w-full justify-start md:justify-center">
                  {[
                    { value: 'documents', label: 'Document Integration' },
                    { value: 'security', label: 'Security & Privacy' },
                    { value: 'csrd', label: 'CSRD' },
                    { value: 'nis2', label: 'NIS2' },
                    { value: 'aifmd', label: 'AIFMD' },
                    { value: 'custom', label: 'Custom Workflows' },
                  ].map(tab => (
                    <TabsTrigger
                      key={tab.value}
                      value={tab.value}
                      className="px-3 py-2 text-base font-medium text-gray-500 border-b-2 border-transparent data-[state=active]:border-emerald-600 data-[state=active]:text-emerald-700 transition-all duration-200 bg-transparent hover:text-emerald-600 rounded-none focus-visible:outline-none focus-visible:ring-0 whitespace-nowrap"
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>

              {/* Tab Content with Apple-like Styling */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="relative z-0"
              >
                <TabsContent value="documents" className="mt-0 w-full">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-2xl p-8 shadow-[0_10px_60px_-15px_rgba(0,0,0,0.1)] border border-gray-100"
                  >
                    <div className="grid md:grid-cols-2 gap-12">
                      <div>
                        <h3 className="text-2xl font-semibold mb-4 text-slate-900">
                          Document Processing with AI
                        </h3>
                        <p className="text-slate-600 mb-8 leading-relaxed">
                          Upload Excel, Word, and PDF files directly into our platform. Our AI
                          instantly analyzes and extracts relevant regulatory data, saving weeks of
                          manual work.
                        </p>
                        <ul className="space-y-4">
                          {documentFeatures.features.map((feature, i) => (
                            <li key={i} className="flex items-start">
                              <div className="bg-emerald-50 rounded-full p-1 mr-3 flex-shrink-0">
                                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                              </div>
                              <span className="text-slate-700">{feature.title}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="relative rounded-xl overflow-hidden shadow-sm bg-slate-50 p-1">
                        <img
                          src="/screens/dashboard_overview.png"
                          alt="Document Processing Dashboard"
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                    </div>
                  </motion.div>
                </TabsContent>

                <TabsContent value="security" className="mt-0 w-full">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-2xl p-8 shadow-[0_10px_60px_-15px_rgba(0,0,0,0.1)] border border-gray-100"
                  >
                    <div className="grid md:grid-cols-2 gap-12">
                      <div>
                        <h3 className="text-2xl font-semibold mb-4 text-slate-900">
                          Enterprise-Grade Security
                        </h3>
                        <p className="text-slate-600 mb-8 leading-relaxed">
                          All sensitive regulatory data is protected with industry-leading security
                          measures including end-to-end encryption and role-based access controls.
                        </p>
                        <ul className="space-y-4">
                          {securityFeatures.features.map((feature, i) => (
                            <li key={i} className="flex items-start">
                              <div className="bg-emerald-50 rounded-full p-1 mr-3 flex-shrink-0">
                                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                              </div>
                              <span className="text-slate-700">{feature.title}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="relative rounded-xl overflow-hidden shadow-sm bg-slate-50 p-1">
                        <img
                          src="/screens/user_profile.png"
                          alt="Security Features"
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                    </div>
                  </motion.div>
                </TabsContent>

                <TabsContent value="csrd" className="mt-0 w-full">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-2xl p-8 shadow-[0_10px_60px_-15px_rgba(0,0,0,0.1)] border border-gray-100"
                  >
                    <div className="grid md:grid-cols-2 gap-12">
                      <div>
                        <h3 className="text-2xl font-semibold mb-4 text-slate-900">
                          CSRD Compliance Toolset
                        </h3>
                        <p className="text-slate-600 mb-8 leading-relaxed">
                          Our comprehensive suite of tools streamlines the entire CSRD compliance
                          process, from data collection to final sustainability reporting.
                        </p>
                        <ul className="space-y-4">
                          {csrdFeatures.features.map((feature, i) => (
                            <li key={i} className="flex items-start">
                              <div className="bg-emerald-50 rounded-full p-1 mr-3 flex-shrink-0">
                                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                              </div>
                              <span className="text-slate-700">{feature.title}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="relative rounded-xl overflow-hidden shadow-sm bg-slate-50 p-1">
                        <img
                          src="/screens/dash1.png"
                          alt="CSRD Compliance Tools"
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                    </div>
                  </motion.div>
                </TabsContent>

                <TabsContent value="nis2" className="mt-0 w-full">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-2xl p-8 shadow-[0_10px_60px_-15px_rgba(0,0,0,0.1)] border border-gray-100"
                  >
                    <div className="grid md:grid-cols-2 gap-12">
                      <div>
                        <h3 className="text-2xl font-semibold mb-4 text-slate-900">
                          NIS2 Cybersecurity Compliance
                        </h3>
                        <p className="text-slate-600 mb-8 leading-relaxed">
                          Meet NIS2 requirements efficiently with our automated risk assessment
                          tools and incident reporting capabilities.
                        </p>
                        <ul className="space-y-4">
                          {nis2Features.features.map((feature, i) => (
                            <li key={i} className="flex items-start">
                              <div className="bg-emerald-50 rounded-full p-1 mr-3 flex-shrink-0">
                                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                              </div>
                              <span className="text-slate-700">{feature.title}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="relative rounded-xl overflow-hidden shadow-sm bg-slate-50 p-1">
                        <img
                          src="/public/images/frameworks/nis2.svg"
                          alt="NIS2 Compliance Tools"
                          className="w-full h-full object-contain rounded-lg"
                        />
                      </div>
                    </div>
                  </motion.div>
                </TabsContent>

                <TabsContent value="aifmd" className="mt-0 w-full">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-2xl p-8 shadow-[0_10px_60px_-15px_rgba(0,0,0,0.1)] border border-gray-100"
                  >
                    <div className="grid md:grid-cols-2 gap-12">
                      <div>
                        <h3 className="text-2xl font-semibold mb-4 text-slate-900">
                          AIFMD Compliance Management
                        </h3>
                        <p className="text-slate-600 mb-8 leading-relaxed">
                          Simplify AIFMD reporting with automated tools that ensure accurate and
                          timely compliance for alternative investment funds.
                        </p>
                        <ul className="space-y-4">
                          {aifmdFeatures.features.map((feature, i) => (
                            <li key={i} className="flex items-start">
                              <div className="bg-emerald-50 rounded-full p-1 mr-3 flex-shrink-0">
                                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                              </div>
                              <span className="text-slate-700">{feature.title}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="relative rounded-xl overflow-hidden shadow-sm bg-slate-50 p-1">
                        <img
                          src="/screens/aifmd/aifguard_1.png"
                          alt="AIFMD Compliance Tools"
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                    </div>
                  </motion.div>
                </TabsContent>

                <TabsContent value="custom" className="mt-0 w-full">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-2xl p-8 shadow-[0_10px_60px_-15px_rgba(0,0,0,0.1)] border border-gray-100"
                  >
                    <div className="grid md:grid-cols-2 gap-12">
                      <div>
                        <h3 className="text-2xl font-semibold mb-4 text-slate-900">
                          Custom AI Workflows
                        </h3>
                        <p className="text-slate-600 mb-8 leading-relaxed">
                          Design bespoke compliance workflows that perfectly match your
                          organization's unique regulatory requirements and business processes.
                        </p>
                        <ul className="space-y-4">
                          {customWorkflowFeatures.features.map((feature, i) => (
                            <li key={i} className="flex items-start">
                              <div className="bg-emerald-50 rounded-full p-1 mr-3 flex-shrink-0">
                                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                              </div>
                              <span className="text-slate-700">{feature.title}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="relative rounded-xl overflow-hidden shadow-sm bg-slate-50 p-1">
                        <img
                          src="/public/images/frameworks/custom-ai.svg"
                          alt="Custom Workflow Tools"
                          className="w-full h-full object-contain rounded-lg"
                        />
                      </div>
                    </div>
                  </motion.div>
                </TabsContent>
              </motion.div>
            </Tabs>
          </div>
        </div>
      </section>

      {/* Detailed Features Section */}
      <section ref={featuresRef} className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900">
              Regulatory Compliance Features
            </h2>
            <p className="text-lg text-slate-600">
              Explore our comprehensive feature set designed to meet all your compliance needs
              across multiple regulatory frameworks
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={controls}
            className="grid md:grid-cols-3 gap-8"
          >
            {allFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-slate-200"
                >
                  <div className="mb-4">
                    <Icon className="h-10 w-10 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-slate-900">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Language Support Section with updated styling */}
      <LanguageSection />

      {/* Call to Action Section */}
      <section className="py-20 bg-emerald-700 text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Transform Regulatory Requirements Into Strategic Advantages?
            </h2>
            <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
              Join forward-thinking organizations that are streamlining their regulatory compliance
              and gaining valuable insights with Verdea.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/book-demo">
                <Button
                  size="lg"
                  className="bg-white text-emerald-700 hover:bg-emerald-50 min-w-[180px]"
                >
                  Book a Demo
                </Button>
              </Link>
              {showSignUp && (
                <Link to="/signup">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-white text-white hover:bg-emerald-600 min-w-[180px]"
                  >
                    Sign Up
                  </Button>
                </Link>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FeaturesPage;
